import { defaultCategoryRepository } from "api/repositories/categories/CategoriesRepository";
import { CategoriesResponseModel } from "api/repositories/categories/model/CategoriesResponseModel";
import { defaultFieldsRepository } from "api/repositories/fields/FieldsRepository";
import { FieldsResponseModel } from "api/repositories/fields/model/FieldsResponseModel";
import { defaultGoodsRepository } from "api/repositories/goods/GoodsRepository";
import { GoodsResponseModel } from "api/repositories/goods/model/GoodsResponseModel";
import { defaultInventoriesRepository } from "api/repositories/inventories/InventoriesRepository";
import { InventoryResponseModel } from "api/repositories/inventories/model/InventoryResponseModel";
import { PartnerResponseModel } from "api/repositories/partners/model/PartnerResponseModel";
import { defaultPartnerManagement } from "api/repositories/partners/PartnerRepository";
import { StoragesResponseModel } from "api/repositories/storages/model/StoragesResponseModel";
import { defaultStoragesRepository } from "api/repositories/storages/StoragesRepository";
import { UserFeatureResponseModel } from "api/repositories/users/model/feature/UserFeatureResponseModel";
import { UsersResponseModel } from "api/repositories/users/model/UsersResponseModel";
import { defaultUserRepository } from "api/repositories/users/UsersRepository";
import { VariantsResponseModel } from "api/repositories/variants/model/VariantsResponseModel";
import { defaultVariantsRepository } from "api/repositories/variants/VariantsRepository";
import { WalletsResponseModel } from "api/repositories/wallets/model/WalletsResponseModel";
import { defaultWalletsRepository } from "api/repositories/wallets/WalletsRepository";
import { SidebarConfigModel } from "api/sidebarConfig/model/SidebarConfigModel";
import SidebarConfigRepository from "api/sidebarConfig/SidebarConfigRepository";
import ls from "localstorage-slim";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";

interface AuthenticationProps {
  onLogin: any;
  onLogout: any;
  token: string;
  user: UsersResponseModel;
  profile: any;
  onSignUp: any;
  isAuthenticated: () => boolean;
  loading: boolean;
  menuConfig: SidebarConfigModel;
  variantsForFilter: VariantsResponseModel[];
  suppliersForFilter: PartnerResponseModel[];
  categoriesForFilter: CategoriesResponseModel[];
  storagesForFilter: StoragesResponseModel[];
  goodsForFilter: GoodsResponseModel[];
  inventoriesForFilter: InventoryResponseModel[];
  fieldsForFilters: FieldsResponseModel[];
  userFeature: UserFeatureResponseModel[];
  walletForFilter: WalletsResponseModel[];
  userRole: string;
  excelLoading: {
    transaction: boolean;
    transactionWarehouse: boolean;
    generals: boolean;
    items: boolean;
    import: boolean;
    export: boolean;
    supplierImmport: boolean;
    mSaleImport: boolean;
  };
  setExcelLoading: Dispatch<
    SetStateAction<{
      transaction: boolean;
      transactionWarehouse: boolean;
      generals: boolean;
      items: boolean;
      import: boolean;
      export: boolean;
      supplierImport: boolean;
      mSaleImport: boolean;
    }>
  >;
  fetchUserFeature: () => Promise<void>
  setWalletForFilter: Dispatch<SetStateAction<WalletsResponseModel[]>>;
  setUserRole: Dispatch<SetStateAction<string>>;
  setUserFeature: Dispatch<SetStateAction<UserFeatureResponseModel[]>>;
  setFieldsForFilters: Dispatch<SetStateAction<FieldsResponseModel[]>>;
  setVariantsForFilter: Dispatch<SetStateAction<VariantsResponseModel[]>>;
  setSuppliersForFilter: Dispatch<SetStateAction<PartnerResponseModel[]>>;
  setCategoriesForFilter: Dispatch<SetStateAction<CategoriesResponseModel[]>>;
  setStoragesForFilter: Dispatch<SetStateAction<StoragesResponseModel[]>>;
  setGoodsForFilter: Dispatch<SetStateAction<GoodsResponseModel[]>>;
  setInventoriesForFilter: Dispatch<SetStateAction<InventoryResponseModel[]>>;
  fetchList: <T extends Object, P extends Object>(
    dataState: Dispatch<SetStateAction<T[]>>,
    type:
      | "variant"
      | "supplier"
      | "category"
      | "storage"
      | "goods"
      | "inventory"
      | "field"
      | "wallet",
    params?: P
  ) => Promise<void>;
}

export const AuthContext = createContext({});

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const [token, setToken] = useState<string | undefined>("");
  const [user, setUser] = useState<UsersResponseModel>({});
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({});
  const [menuConfig, setMenuConfig] = useState<SidebarConfigModel>();
  const [variantsForFilter, setVariantsForFilter] = useState<
    VariantsResponseModel[]
  >([]);
  const [suppliersForFilter, setSuppliersForFilter] = useState<
    PartnerResponseModel[]
  >([]);
  const [categoriesForFilter, setCategoriesForFilter] = useState<
    CategoriesResponseModel[]
  >([]);
  const [storagesForFilter, setStoragesForFilter] = useState<
    StoragesResponseModel[]
  >([]);
  const [goodsForFilter, setGoodsForFilter] = useState<GoodsResponseModel[]>(
    []
  );
  const [inventoriesForFilter, setInventoriesForFilter] = useState<
    InventoryResponseModel[]
  >([]);

  const [fieldsForFilters, setFieldsForFilters] = useState<
    FieldsResponseModel[]
  >([]);

  const [walletForFilter, setWalletForFilter] = useState<
    WalletsResponseModel[]
  >([]);

  const [userFeature, setUserFeature] = useState<UserFeatureResponseModel[]>(
    []
  );
  const [userRole, setUserRole] = useState<string>("");
  const [excelLoading, setExcelLoading] = useState<{
    transaction: boolean;
    transactionWarehouse: boolean;
    generals: boolean;
    items: boolean;
    import: boolean;
    export: boolean;
    supplierImmport: boolean;
    mSaleImport: boolean;
  }>({
    generals: false,
    items: false,
    transaction: false,
    transactionWarehouse: false,
    export: false,
    import: false,
    mSaleImport: false,
    supplierImmport: false,
  });
  ls.config.encrypt = true;

  const fetchList = async <T extends Object, P extends Object>(
    dataState: Dispatch<SetStateAction<T[]>>,
    type:
      | "variant"
      | "supplier"
      | "category"
      | "storage"
      | "goods"
      | "inventory"
      | "wallet"
      | "field",
    params?: P
  ) => {
    try {
      let res = undefined;
      switch (type) {
        case "variant":
          res = await defaultVariantsRepository?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
        case "supplier":
          res = await defaultPartnerManagement?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
        case "category":
          res = await defaultCategoryRepository?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
        case "storage":
          res = await defaultStoragesRepository?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
        case "goods":
          res = await defaultGoodsRepository?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
        case "inventory":
          res = await defaultInventoriesRepository?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
        case "field":
          res = await defaultFieldsRepository?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
        case "wallet":
          res = await defaultWalletsRepository?.getList(
            !params ? { pagination: false } : params
          );
          if (res) {
            dataState(res?.data);
          }
          break;
      }
      return res?.data;
    } catch (error) {
      console.log(error);
    }
  };

  const getSidebar = useCallback(
    async (feature: typeof userFeature) => {
      try {
        const siderConfig = await SidebarConfigRepository?.getSidebarMenu(
          feature
        );
        setMenuConfig(siderConfig?.data);
      } catch (error) {
        console.log(error);
      }
    },
    [navigate]
  );

  const fetchUserFeature = async () => {
    try {
      const res = await defaultUserRepository?.getFeature();
      if (res) {
        setUserFeature(res?.data);
        ls.set("warehouse-feature", JSON.stringify(res?.data));
        await getSidebar(res?.data);
      }
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("warehouse-cms-token");
    let user = JSON.parse(ls.get("user")!);
    let features = JSON.parse(ls.get("warehouse-feature")!);
    if (!token || !user || !features) {
      onLogout();
    }
    if (!!token && !!features && !!user) {
      setUserFeature(features);
      getSidebar(features);
      setToken(token);
      setUser(user);
      setUserRole(user?.role?.code);
    }
  }, []);

  const onLogin = async (user: UsersResponseModel, token: string) => {
    localStorage.setItem("warehouse-cms-token", token);
    setToken(token);
    setUserRole(user?.role?.code);
    ls.set("user", JSON.stringify(user));
    setUser(user);
    await fetchUserFeature();
    navigate("/history/transactions-history");
  };

  const onSignUp = async (user: UsersResponseModel) => {
    localStorage.setItem("user", JSON.stringify(user));
    setUser(user);
    navigate("/sign-in");
  };

  const onLogout = () => {
    localStorage.clear();
    setToken(undefined);
    setUser(undefined);
    navigate("/sign-in");
  };

  const isAuthenticated = () => {
    return !!token && !!user;
  };

  return (
    <AuthContext.Provider
      value={{
        loading,
        profile,
        user,
        token,
        menuConfig,
        variantsForFilter,
        suppliersForFilter,
        categoriesForFilter,
        storagesForFilter,
        goodsForFilter,
        inventoriesForFilter,
        fieldsForFilters,
        userFeature,
        userRole,
        walletForFilter,
        excelLoading,
        setExcelLoading,
        setWalletForFilter,
        setUserFeature,
        setUserRole,
        setFieldsForFilters,
        onLogout,
        isAuthenticated,
        onLogin,
        onSignUp,
        setVariantsForFilter,
        setSuppliersForFilter,
        setCategoriesForFilter,
        setStoragesForFilter,
        setGoodsForFilter,
        setInventoriesForFilter,
        fetchList,
        fetchUserFeature
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthenticationProps =>
  useContext(AuthContext) as AuthenticationProps;
